/// Powerboat handling at speed — RYA Powerboat Intermediate +
/// Advanced syllabus content. Six domains:
///
///   1. Planing — what it is, the planing threshold, the modes of
///      operation (displacement / transitional / planing).
///   2. Trim + trim tabs — bow up / bow down, when to use each.
///   3. Sea-state handling — beam-on chop, head seas, following
///      seas + broach risk.
///   4. Stopping distance — typical stopping distances at different
///      speeds; the controlled stop manoeuvre.
///   5. Turning + cornering — banking, low-speed pivot, high-speed
///      sweep.
///   6. Quick-reference handling tips — kill cord, brief, lookout.

// ───────────────────────────────────────────────────────────────────
// 1. Planing — modes of operation
// ───────────────────────────────────────────────────────────────────

export type HullMode = "displacement" | "transition" | "planing";

export interface HullModeInfo {
  id: HullMode;
  name: string;
  /// Typical speed range for a 6 m / 30 kt-capable RIB.
  speedRangeKnots: string;
  /// What the hull is doing in the water.
  behaviour: string;
  /// Fuel + handling notes.
  notes: string;
}

export const HULL_MODES: readonly HullModeInfo[] = Object.freeze([
  {
    id: "displacement",
    name: "Displacement mode",
    speedRangeKnots: "0 – ~6 kt for a 6 m RIB; longer hulls go faster",
    behaviour:
      "Hull pushing water aside, bow wake forming at the stem, stern wake forming at the transom. Boat sits with its waterline approximately level with the painted waterline.",
    notes:
      "Maximum hull speed limited by ~1.34 × √waterline-length (kts). Pushing past this hull speed in displacement mode dumps fuel without speed gain — bow rises, stern squats, drag explodes.",
  },
  {
    id: "transition",
    name: "Transition (hump)",
    speedRangeKnots: "Briefly between hull-speed + planing threshold (~6-12 kt typical)",
    behaviour:
      "Bow LIFTING out of the water as the boat starts to climb its bow wave. Maximum drag region. Engine running hard, lots of noise + fuel burn, modest speed gain.",
    notes:
      "Avoid sitting in transition mode — either go full throttle to break onto plane, or back off to displacement mode. Stern trim tabs DOWN help break through the hump; trim up tucks the bow down briefly.",
  },
  {
    id: "planing",
    name: "Planing",
    speedRangeKnots: "Above ~12-15 kt for a typical 6-8 m RIB; modern fast RIBs achieve plane at 18-20 kt",
    behaviour:
      "Hull RIDING on top of the water surface, supported by hydrodynamic lift on the planing surface rather than displacement buoyancy. Low wetted area + low drag.",
    notes:
      "Efficient + fast. Fuel burn per nautical mile DROPS once on plane vs transitioning. Boat handles + responds differently — small steering inputs at high speed have big consequences.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 2. Trim + trim tabs
// ───────────────────────────────────────────────────────────────────

export type TrimDirection = "bow-up" | "bow-down" | "neutral";

export interface TrimSetting {
  id: TrimDirection;
  name: string;
  /// What the trim tabs / outboard tilt are doing.
  appliedBy: string;
  /// Boat attitude that results.
  attitude: string;
  /// When you'd choose this trim.
  whenToUse: string;
  /// Pitfalls of incorrect trim.
  pitfalls: readonly string[];
}

export const TRIM_SETTINGS: readonly TrimSetting[] = Object.freeze([
  {
    id: "bow-down",
    name: "Bow DOWN (trim tabs down, outboard trimmed in)",
    appliedBy:
      "Trim tabs deployed downward; outboard or sterndrive tilted IN (toward the transom). Drives the bow into the water.",
    attitude:
      "Bow LOWERED — flat planing attitude. More hull in the water; more drag but smoother ride.",
    whenToUse:
      "Breaking onto plane (climbing the hump); running into beam-on chop where you want the bow down to slice through; low-light / spray conditions where you want to see ahead clearly.",
    pitfalls: [
      "Stuffing the bow into a swell — too much bow-down + the bow buries in following seas, dramatically increasing broach risk.",
      "Tabs down at high speed creates massive drag + heats the engine.",
    ],
  },
  {
    id: "neutral",
    name: "Neutral",
    appliedBy:
      "Tabs retracted; outboard at mid-trim. Boat sits on its designed planing attitude.",
    attitude:
      "Bow + transom both clear of the water; angle of attack ~3-5° above horizontal.",
    whenToUse:
      "Open water with flat or moderate sea; cruising at planing speeds; the default cruise setting.",
    pitfalls: [
      "Forgetting to re-trim when conditions change — bow-up in flat water is wasteful; bow-down in head seas is uncomfortable + dangerous.",
    ],
  },
  {
    id: "bow-up",
    name: "Bow UP (trim tabs up / outboard trimmed out)",
    appliedBy:
      "Trim tabs raised; outboard tilted OUT (away from transom). Drives the bow up + away from the water.",
    attitude:
      "Bow RAISED — less hull in the water; less drag; speed gain at the cost of comfort.",
    whenToUse:
      "Maximum speed in flat water; running with following sea where you want the bow OUT to avoid stuffing.",
    pitfalls: [
      "Porpoising — bow-up too much + boat starts hopping rhythmically; reduce bow-up + speed will smooth out.",
      "Loss of forward visibility — bow blocks view of the water ahead; bad in busy waters.",
      "Beam-on chop with bow-up — boat slams from wave-to-wave; uncomfortable + hull-fatiguing.",
    ],
  },
]);

// ───────────────────────────────────────────────────────────────────
// 3. Sea-state handling
// ───────────────────────────────────────────────────────────────────

export type SeaCondition =
  | "head-seas"
  | "beam-on-chop"
  | "following-seas"
  | "quartering-seas"
  | "wash-from-other-vessel";

export interface SeaHandling {
  id: SeaCondition;
  name: string;
  /// What's happening physically.
  description: string;
  /// How the boat handles the condition.
  handling: string;
  /// Boat speed + trim recommendation.
  recommended: string;
  /// What NOT to do.
  pitfalls: readonly string[];
}

export const SEA_HANDLING: readonly SeaHandling[] = Object.freeze([
  {
    id: "head-seas",
    name: "Head seas (waves on the bow)",
    description:
      "Waves approaching from ahead. The boat punches into each wave + slows on impact; you climb the back of the next wave + accelerate down.",
    handling:
      "Most predictable sea state — boat rises + falls with each wave. Pitch motion only, no roll. Tiring at high speed.",
    recommended:
      "Speed: 15-20 kt for a typical RIB in F4 head seas; slower in larger swells. Bow DOWN slightly to slice into waves; throttle off each crest to soften the landing.",
    pitfalls: [
      "Full throttle into head seas — slam landings break crew + boat; back off the throttle as each crest passes.",
      "Bow-up trim — boat slams on each landing instead of slicing.",
      "Not strapped in — crew flying out of seats at hull-jarring impacts. Use the grab handles; keep weight low.",
    ],
  },
  {
    id: "beam-on-chop",
    name: "Beam-on chop (waves on the beam)",
    description:
      "Waves approaching from the side. The boat is rolled across the beam; spray washes the cockpit; uncomfortable + unsafe at speed.",
    handling:
      "Worst sea state for fast powerboats. Rolling motion can throw crew out; risk of breaking windscreens or open cockpit superstructure.",
    recommended:
      "Speed: REDUCE to displacement or slow transition speeds (6-8 kt). Alter course to angle the waves on the bow at 20-30° — much more comfortable. Trim NEUTRAL.",
    pitfalls: [
      "Trying to stay on plane in beam-on chop — boat slams sideways with each wave + risks rolling.",
      "Not changing course to angle to the seas — accept that the direct route to your destination is now via diagonal legs.",
      "Crew on the windward side — get them on the LEEWARD side + low.",
    ],
  },
  {
    id: "following-seas",
    name: "Following seas (waves from astern)",
    description:
      "Waves overtaking the boat from behind. The stern lifts as each wave passes underneath; the bow can stuff into the back of the wave ahead; risk of broaching or pitchpoling.",
    handling:
      "Most dangerous sea state for fast powerboats. The classic broach happens when the stern is lifted, the bow stuffs into the wave ahead, the boat slews sideways + capsizes.",
    recommended:
      "Speed: MATCH the wave speed (stay slightly slower or slightly faster, NEVER exactly the wave speed). Bow UP via trim. Stay alert + steer ACTIVELY to prevent yaw.",
    pitfalls: [
      "Wave speed matched exactly — boat surfs the wave, becomes unsteerable, broaches.",
      "Coming OFF the back of a wave too fast — bow buries in the trough.",
      "Steering passively + letting the boat yaw — broach develops in seconds + can't be recovered at high speed.",
      "Throwing off the throttle when the bow stuffs — actually accelerates the broach. Apply opposite throttle + steer ahead.",
    ],
  },
  {
    id: "quartering-seas",
    name: "Quartering seas (waves on the quarter)",
    description:
      "Waves approaching from 45° astern (the rear quarter). Compound motion — pitching + rolling + yawing all at once.",
    handling:
      "Most uncomfortable sea state. Boat moves in 3D + the helmsman has to actively counter each wave's rotation.",
    recommended:
      "Speed: similar to following seas; trim NEUTRAL or slight bow-up. Helmsman MUST stay on the wheel — autopilot cannot handle quartering seas safely.",
    pitfalls: [
      "Autopilot left on — autopilot can't anticipate the wave + the boat broaches.",
      "Crew weight wrong — crew lurches with each wave; secure them to seats.",
    ],
  },
  {
    id: "wash-from-other-vessel",
    name: "Wash from another vessel",
    description:
      "Two or three large waves close together from the bow of a passing fast vessel (commercial RIB, fast ferry, large yacht).",
    handling:
      "Surprise factor — appears suddenly, then gone in 10-20 seconds. Larger than expected for the wind / sea state.",
    recommended:
      "Throttle OFF to displacement speed before the wash arrives. Present the BOW to the wash (turn into it 20-30°). Brace crew. Accept the rough 5-10 seconds.",
    pitfalls: [
      "Staying at planing speed through wash — boat slams, hull damages, crew flung.",
      "Wash on the beam — risk of rolling; turn into it ALWAYS.",
      "Not bracing crew — wash arrives unannounced + crew thrown forward into windscreens.",
    ],
  },
]);

// ───────────────────────────────────────────────────────────────────
// 4. Stopping distance + the controlled stop
// ───────────────────────────────────────────────────────────────────

export interface StoppingDistance {
  /// Boat speed in knots.
  speedKnots: number;
  /// Approximate stopping distance for a 6 m RIB / sportsboat, in
  /// boat lengths.
  distanceBoatLengths: number;
  /// Time to stop, in seconds.
  timeSeconds: number;
  /// Operational note.
  note: string;
}

export const STOPPING_DISTANCES: readonly StoppingDistance[] = Object.freeze([
  {
    speedKnots: 5,
    distanceBoatLengths: 1,
    timeSeconds: 2,
    note: "Idle speed — boat coasts to a stop quickly with throttle off.",
  },
  {
    speedKnots: 10,
    distanceBoatLengths: 3,
    timeSeconds: 4,
    note: "Below planing speed. Throttle off + boat decelerates predictably.",
  },
  {
    speedKnots: 20,
    distanceBoatLengths: 8,
    timeSeconds: 7,
    note: "On plane. Throttle off + the boat drops off plane in ~2 seconds + then decelerates more slowly.",
  },
  {
    speedKnots: 30,
    distanceBoatLengths: 18,
    timeSeconds: 10,
    note: "Fast planing. Throttle off + the boat plunges through the bow wave + decelerates with a marked nose-down attitude.",
  },
  {
    speedKnots: 40,
    distanceBoatLengths: 28,
    timeSeconds: 13,
    note: "Maximum cruise. Stopping distance is significant; plan stops well in advance + use astern propulsion if available.",
  },
]);

/// The controlled-stop manoeuvre — what the helmsman actually does
/// when they need to stop quickly.
export const CONTROLLED_STOP: readonly string[] = Object.freeze([
  "Brief crew — \"stopping\" — so they brace. Untrained crew thrown forward in fast stop.",
  "Throttle to neutral — NOT all the way back unless astern is needed urgently.",
  "Boat drops off plane in ~2 seconds; bow rises briefly as the stern wave catches up.",
  "Boat then decelerates from sub-planing speed to displacement + stop.",
  "If a faster stop is needed: throttle astern after the boat is off plane. NEVER apply astern at planing speed — propellers cavitate + drives are loaded backwards through gearboxes.",
  "Wait for forward motion to stop before any astern manoeuvring.",
]);

// ───────────────────────────────────────────────────────────────────
// 5. Turning at speed
// ───────────────────────────────────────────────────────────────────

export interface TurningMode {
  id: "low-speed-pivot" | "planing-sweep" | "banked-turn";
  name: string;
  description: string;
  whenToUse: string;
  pitfalls: readonly string[];
}

export const TURNING_MODES: readonly TurningMode[] = Object.freeze([
  {
    id: "low-speed-pivot",
    name: "Low-speed pivot (close-quarters)",
    description:
      "At displacement / idle speed, the boat pivots around its centre of buoyancy. Useful for tight turns in marinas, approaching a berth, picking up an MOB.",
    whenToUse:
      "Marina manoeuvring, MOB approach, slipping out of a berth, prop-walk in reverse.",
    pitfalls: [
      "Forgetting prop-walk — outboard / sterndrive walks the stern sideways in reverse; use it to your advantage but anticipate it.",
      "Wind on the bow — pivots the boat unpredictably. Use power against it.",
    ],
  },
  {
    id: "planing-sweep",
    name: "Planing sweep — broad turn",
    description:
      "On plane, the boat turns by sweeping a wide arc. The hull banks slightly into the turn (inwards) + the helmsman must apply opposite rudder to prevent over-banking.",
    whenToUse:
      "Course changes at cruise; clearing traffic; following a curving channel at speed.",
    pitfalls: [
      "Too sharp a turn at planing speed — boat over-banks, the inside chine catches the water, boat can capsize.",
      "Too slow a turn for the speed — leaves a large turning circle that may impact other vessels or shoal water.",
    ],
  },
  {
    id: "banked-turn",
    name: "Banked turn — high speed",
    description:
      "At high speed, the boat banks sharply inward. The inside chine becomes the rear pivot; the boat carves a tight arc. Helmsman must coordinate throttle (light pressure on), helm (smooth + progressive) + crew weight (low + inboard).",
    whenToUse:
      "Performance manoeuvres, dodging an obstacle, fast course reversal. Requires practice in benign conditions.",
    pitfalls: [
      "Crew weight on the wrong side — outside crew weight makes the boat heel further + risk capsize.",
      "Too tight at full power — chine catches, boat spins + may capsize. ALWAYS apply throttle slightly first to lift the bow before turning.",
      "Following a tight banked turn with a hard throttle-back — drops the boat off plane mid-turn + can broach.",
    ],
  },
]);
